import {
  useQuery,
  useMutation,
  QueryFunction,
  MutationFunction,
  UseMutationResult,
  useQueryClient,
  keepPreviousData,
  UseQueryOptions,
} from '@tanstack/react-query'
import { client } from '../client/index'

export type Name = {
  firstName?: string
  lastName?: string
  companyName?: string
  additionalName?: string
}
export type Email = {
  address?: string
}

export type Address = {
  countryCode?: string
  zipCode?: string
  city?: string
  street?: string
  number: string
  additionalAddressLine?: string
  addition?: string
  isBusiness?: boolean
  additionalInfo?: string
}

export interface AddressBookEntry {
  id: string | null
  name: Name
  email?: Email
  address: Address
  tags: string[]
  phoneNumber?: string
  vatNumber?: string
}

/**
 * Get all addressbook entries
 */

type GetAddressBookEntriesProps = {
  searchTerm?: string
  maxContacts?: number
  enabled?: boolean
  signal?: AbortSignal
}

function getAddressBookEntries({
  searchTerm,
  maxContacts = 100,
  signal,
}: GetAddressBookEntriesProps): Promise<AddressBookEntry[]> {
  const params: string[][] = [
    ['max', `${maxContacts}`],
    ['query', `${searchTerm}`],
  ]
  return client<AddressBookEntry[]>({ url: '', params, method: 'get', signal })
}

export const getAddressBookEntriesQueryKey = () => '/address-book'

export function useGetAddressBookEntries<TData = AddressBookEntry[], TError = unknown>(
  args?: GetAddressBookEntriesProps,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAddressBookEntries>>, TError, TData>>
  }
) {
  const { searchTerm = '', maxContacts } = args || {}
  const queryFn: QueryFunction<AddressBookEntry[]> = ({ signal }) =>
    getAddressBookEntries({ searchTerm, maxContacts, signal })
  const queryKey = [getAddressBookEntriesQueryKey(), searchTerm, maxContacts]

  return useQuery({
    queryKey,
    queryFn,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    ...(options?.query ?? {}),
  })
}

/**
 * Get single addressbook entry
 */

type GetAddressBookEntryProps = {
  id?: string
  signal?: AbortSignal
}

function getAddressBookEntry({ id, signal }: GetAddressBookEntryProps): Promise<AddressBookEntry> {
  return client<AddressBookEntry>({ url: `/${id}`, method: 'get', signal })
}

export function useGetAddressBookEntry({ id }: GetAddressBookEntryProps) {
  const queryFn: QueryFunction<AddressBookEntry> = ({ signal }) => getAddressBookEntry({ id, signal })
  const queryKey = [getAddressBookEntriesQueryKey(), id]
  return useQuery({ queryKey, queryFn, enabled: !!id })
}

/**
 * Add new addresbook entry
 */
export type MutateAddressBookEntry = Omit<AddressBookEntry, 'id' | 'tags'>

export function useAddAddressBookEntry(): UseMutationResult<AddressBookEntry, string, MutateAddressBookEntry> {
  const queryClient = useQueryClient()
  const mutationFn: MutationFunction<AddressBookEntry, MutateAddressBookEntry> = (newAddressBookEntry) =>
    client<AddressBookEntry>({ url: '', method: 'post', data: newAddressBookEntry })
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [getAddressBookEntriesQueryKey()],
      })
    },
  })
}

/**
 * Update addressbook entry
 */
type UpdateAddressBookEntryProps = {
  id?: string
  data?: MutateAddressBookEntry
  signal?: AbortSignal
}

function updateAddressBookEntry({ id, data, signal }: UpdateAddressBookEntryProps): Promise<AddressBookEntry> {
  return client<AddressBookEntry>({ url: `/${id}`, method: 'put', data, signal })
}

export function useUpdateAddressBookEntry({
  id,
}: UpdateAddressBookEntryProps): UseMutationResult<AddressBookEntry, string, UpdateAddressBookEntryProps> {
  const queryClient = useQueryClient()
  const mutationFn: MutationFunction<AddressBookEntry, UpdateAddressBookEntryProps> = ({ data, signal }) =>
    updateAddressBookEntry({ id, data, signal })

  return useMutation({
    mutationKey: ['/address-book', id],
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [getAddressBookEntriesQueryKey()],
      })
    },
  })
}

/**
 * Delete addressbook entry
 */
type DeleteAddressBookEntryProps = {
  id?: string
  signal?: AbortSignal
}

export function useDeleteAddressBookEntry({
  id,
}: DeleteAddressBookEntryProps): UseMutationResult<unknown, string, DeleteAddressBookEntryProps> {
  const queryClient = useQueryClient()
  const mutationFn: MutationFunction<unknown, DeleteAddressBookEntryProps> = () =>
    client<unknown>({ url: `/${id}`, method: 'delete' })

  return useMutation({
    mutationKey: ['/address-book', id],
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [getAddressBookEntriesQueryKey()],
      })
    },
  })
}
